import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { CustomToast } from '../../../helpers/toast';
import LogoColegio from '../../../assets/img/logoColegio.png';
import globalInformation from '../../../helpers/globalInformation';
import { getAllConfiguraciones } from '../../../features/configuracionSlice';

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import {
  obtenerConsolidadoGeneralPorGrado,
} from '../../../features/notaSlice';

// Función para obtener el color según la calificación
const getGradeColor = grade => {
  if (grade >= 4.7) return '#27AE60'; // Verde oscuro - Superior
  if (grade >= 4.0) return '#2ECC71'; // Verde - Alto
  if (grade >= 3.0) return '#F39C12'; // Naranja - Básico
  return '#E74C3C'; // Rojo - Bajo
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontSize: 7,
    fontFamily: 'Helvetica',
  },

  // Encabezado institucional
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
    borderBottomWidth: 2,
    borderBottomColor: '#2C3E50',
    paddingBottom: 5,
  },

  logoContainer: {
    width: 40,
    height: 40,
    marginRight: 10,
  },

  schoolInfoContainer: {
    alignItems: 'center',
  },

  schoolName: {
    fontSize: 10,
    fontWeight: 700,
    color: '#2C3E50',
    marginBottom: 1,
    textAlign: 'center',
  },

  schoolDecrees: {
    fontSize: 5.5,
    color: '#34495E',
    marginBottom: 0.5,
    textAlign: 'center',
  },

  schoolCode: {
    fontSize: 6,
    fontWeight: 600,
    color: '#34495E',
    marginTop: 1,
    textAlign: 'center',
  },

  reportTitle: {
    fontSize: 8,
    fontWeight: 900,
    color: '#0f3460',
    marginTop: 1,
    marginBottom: 1,
    textAlign: 'center',
  },

  // Información del reporte
  reportInfoContainer: {
    flexDirection: 'row',
    gap: 2,
    marginBottom: 4,
    backgroundColor: '#f0f3f7',
    padding: 3,
    borderRadius: 2,
    borderLeftWidth: 3,
    borderLeftColor: '#2fa33a',
  },
  infoBox: {
    flex: 1,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRightWidth: 1,
    borderRightColor: '#d0d9e3',
  },

  infoBoxLast: {
    borderRightWidth: 0,
  },

  infoLabel: {
    fontSize: 6,
    fontWeight: 900,
    color: '#0f3460',
    textTransform: 'uppercase',
    marginBottom: 0.5,
  },

  infoValue: {
    fontSize: 5.5,
    fontWeight: 600,
    color: '#2fa33a',
  },

  // Tabla principal
  tableContainer: {
    borderWidth: 1.5,
    borderColor: '#1e3c72',
    borderRadius: 2,
    overflow: 'hidden',
  },

  // Encabezado principal
  mainHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1.5,
    borderBottomColor: '#1e3c72',
    backgroundColor: '#1e3c72',
    minHeight: 16,
  },
  headerColNum: {
    width: '3%',
    padding: 2.5,
    borderRightWidth: 1,
    borderRightColor: '#1e3c72',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerColName: {
    width: '15%',
    padding: 2.5,
    borderRightWidth: 1,
    borderRightColor: '#1e3c72',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerColCurso: {
    flex: 1,
    padding: 2,
    borderRightWidth: 1,
    borderRightColor: '#1e3c72',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerColPromedio: {
    width: '7%',
    padding: 2.5,
    borderRightWidth: 1,
    borderRightColor: '#1e3c72',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerColMerito: {
    width: '4%',
    padding: 2.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerMainText: {
    fontSize: 6,
    fontWeight: 800,
    textAlign: 'center',
    color: '#FFFFFF',
    letterSpacing: 0.2,
  },

  // Encabezado de sub-notas por curso
  subHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1.2,
    borderBottomColor: '#4a6fa5',
    backgroundColor: '#e8eef5',
    minHeight: 14,
  },
  subHeaderCell: {
    flex: 1,
    padding: 1.5,
    borderRightWidth: 0.8,
    borderRightColor: '#4a6fa5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subHeaderText: {
    fontSize: 5.5,
    fontWeight: 700,
    textAlign: 'center',
    color: '#1e3c72',
  },

  // Filas de datos
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.8,
    borderBottomColor: '#dde5f0',
    minHeight: 15,
  },
  dataRowAlt: {
    backgroundColor: '#f9fafc',
  },
  dataRowEven: {
    backgroundColor: '#FFFFFF',
  },
  dataCellNum: {
    width: '3%',
    padding: 1.5,
    borderRightWidth: 1,
    borderRightColor: '#dde5f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dataCellName: {
    width: '15%',
    padding: 1.5,
    paddingLeft: 2.5,
    borderRightWidth: 1,
    borderRightColor: '#dde5f0',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  dataCellNota: {
    flex: 1,
    padding: 1,
    borderRightWidth: 0.8,
    borderRightColor: '#dde5f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dataCellPromedio: {
    width: '7%',
    padding: 1.5,
    borderRightWidth: 1,
    borderRightColor: '#dde5f0',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fffbf0',
  },
  dataCellMerito: {
    width: '4%',
    padding: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Estilos de texto
  dataText: {
    fontSize: 5.5,
    color: '#2c3e50',
    fontWeight: 500,
  },
  dataTextBold: {
    fontSize: 5.5,
    fontWeight: 700,
    color: '#0f3460',
  },
  notaCell: {
    fontSize: 5.5,
    fontWeight: 700,
  },
  nameText: {
    fontSize: 5.5,
    color: '#0f3460',
    fontWeight: 600,
  },
  meritoText: {
    fontSize: 5.5,
    fontWeight: 800,
    color: '#2fa33a',
    textAlign: 'center',
  },

  // Escala de valoración
  scaleContainer: {
    marginTop: 3,
    marginBottom: 3,
    padding: 3,
    backgroundColor: '#f8f9fa',
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#e5e7e9',
  },

  scaleTitle: {
    fontSize: 5.5,
    fontWeight: 800,
    color: '#0f3460',
    marginBottom: 2,
    textAlign: 'center',
  },

  scaleRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 2,
  },

  scaleItem: {
    flex: 1,
    padding: 2,
    borderRadius: 2,
    alignItems: 'center',
  },

  scaleItemText: {
    fontSize: 5,
    fontWeight: 700,
    color: 'white',
    textAlign: 'center',
  },

  scaleRange: {
    fontSize: 4.5,
    fontWeight: 600,
    color: 'white',
    textAlign: 'center',
    marginTop: 0.5,
  },

  // Pie de página
  footerContainer: {
    marginTop: 6,
    paddingTop: 4,
    borderTopWidth: 1.5,
    borderTopColor: '#dde5f0',
  },
  footerText: {
    fontSize: 6.5,
    color: '#34495E',
    marginBottom: 1.5,
    fontWeight: 600,
  },
  footerValue: {
    fontSize: 6.5,
    fontWeight: 800,
    color: '#1e3c72',
  },
});

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = API_URL && API_URL.split('/api')[0];

const ConsolidadoGeneralReporte = ({ data, grado, sede, tutor, anio, configuracion }) => {
  // Obtener materias únicas y ordenarlas
  const materias = [
    ...new Set(
      data.flatMap(item => item.notas.map(nota => nota.materiaNombre))
    ),
  ].sort();

  const logoToUse = configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : LogoColegio;

  // Dividir materias en grupos para múltiples páginas
  const MATERIAS_PRIMERA_PAGINA = 6; // Primera página con nombres
  const MATERIAS_PAGINAS_SIGUIENTES = 8; // Páginas siguientes sin nombres (más espacio)

  const gruposMaterias = [];
  if (materias.length <= MATERIAS_PRIMERA_PAGINA) {
    // Si caben todas en una página, mostrar todo junto
    gruposMaterias.push(materias);
  } else {
    // Primera página con menos materias (porque incluye nombres)
    gruposMaterias.push(materias.slice(0, MATERIAS_PRIMERA_PAGINA));

    // Páginas siguientes con más materias (sin nombres)
    for (let i = MATERIAS_PRIMERA_PAGINA; i < materias.length; i += MATERIAS_PAGINAS_SIGUIENTES) {
      gruposMaterias.push(materias.slice(i, i + MATERIAS_PAGINAS_SIGUIENTES));
    }
  }

  // Ordenar estudiantes por apellidos
  const sortedData = [...data].sort((a, b) =>
    `${a.estudiante.apellidos} ${a.estudiante.nombres}`.localeCompare(
      `${b.estudiante.apellidos} ${b.estudiante.nombres}`
    )
  );

  // Calcular promedios
  const dataConPromedio = sortedData.map(item => {
    const notasValidas = item.notas.filter(
      n => n.promedio != null && n.promedio > 0
    );
    const promedioGeneral =
      notasValidas.length > 0
        ? notasValidas.reduce((sum, n) => sum + n.promedio, 0) /
        notasValidas.length
        : 0;
    return {
      ...item,
      promedioGeneral,
    };
  });

  // Ordenar por promedio para calcular mérito
  const dataOrdenadaPorMerito = [...dataConPromedio].sort(
    (a, b) => b.promedioGeneral - a.promedioGeneral
  );

  // Calcular promedio del grado
  const promedioDelGrado =
    dataConPromedio.length > 0
      ? (
        dataConPromedio.reduce((sum, item) => sum + item.promedioGeneral, 0) /
        dataConPromedio.length
      ).toFixed(2)
      : 0;

  return (
    <Document>
      {gruposMaterias.map((grupoMaterias, pageIndex) => {
        const esPrimeraPagina = pageIndex === 0;
        const esUltimaPagina = pageIndex === gruposMaterias.length - 1;
        const mostrarNombres = esPrimeraPagina;
        const mostrarPromedioYRanking = gruposMaterias.length === 1 || esUltimaPagina;

        return (
          <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page}>
            {/* Encabezado con información de la institución */}
            <View style={styles.headerContainer}>
              <View style={styles.logoContainer}>
                <Image src={logoToUse} />
              </View>
              <View style={styles.schoolInfoContainer}>
                <Text style={styles.schoolName}>
                  {configuracion?.nombreColegio || globalInformation?.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO'}
                </Text>
                {globalInformation?.decretos?.map((decreto, index) => (
                  <Text key={index} style={styles.schoolDecrees}>
                    {decreto?.tipo} N° {decreto.numero} del {decreto.fecha}
                  </Text>
                ))}
                <Text style={styles.schoolCode}>SEDE: {sede}</Text>
              </View>
            </View>

            {/* Título del reporte centrado */}
            <Text style={styles.reportTitle}>CONSOLIDADO GENERAL DE CALIFICACIONES</Text>

            {/* Información del Reporte */}
            <View style={styles.reportInfoContainer}>
              <View style={styles.infoBox}>
                <Text style={styles.infoLabel}>Grado</Text>
                <Text style={styles.infoValue}>{grado}</Text>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.infoLabel}>Año Académico</Text>
                <Text style={styles.infoValue}>{anio}</Text>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.infoLabel}>Docente Titular</Text>
                <Text style={styles.infoValue}>{tutor}</Text>
              </View>
              <View style={[styles.infoBox, styles.infoBoxLast]}>
                <Text style={styles.infoLabel}>Total</Text>
                <Text style={styles.infoValue}>{dataConPromedio.length}</Text>
              </View>
            </View>

            {/* Tabla principal */}
            <View style={styles.tableContainer}>
              {/* Encabezado principal */}
              <View style={styles.mainHeaderRow}>
                {mostrarNombres && (
                  <>
                    <View style={styles.headerColNum}>
                      <Text style={styles.headerMainText}>N°</Text>
                    </View>
                    <View style={styles.headerColName}>
                      <Text style={styles.headerMainText}>ESTUDIANTE</Text>
                    </View>
                  </>
                )}
                {grupoMaterias.map((materia, idx) => (
                  <View key={idx} style={styles.headerColCurso}>
                    <Text style={styles.headerMainText}>{materia}</Text>
                  </View>
                ))}
                {mostrarPromedioYRanking && (
                  <>
                    <View style={styles.headerColPromedio}>
                      <Text style={styles.headerMainText}>PROMEDIO</Text>
                    </View>
                    <View style={styles.headerColMerito}>
                      <Text style={styles.headerMainText}>POS.</Text>
                    </View>
                  </>
                )}
              </View>

              {/* Encabezado de sub-notas (P1, P2, P3, P4, PROM, REC) */}
              <View style={styles.subHeaderRow}>
                {mostrarNombres && (
                  <>
                    <View style={styles.dataCellNum}></View>
                    <View style={styles.dataCellName}></View>
                  </>
                )}
                {grupoMaterias.map((materia, idx) => (
                  <View
                    key={idx}
                    style={{
                      flexDirection: 'row',
                      flex: 1,
                      borderRightWidth: 0.8,
                      borderRightColor: '#4a6fa5',
                    }}
                  >
                    <View style={[styles.subHeaderCell, { borderRightWidth: 0.6 }]}>
                      <Text style={styles.subHeaderText}>P1</Text>
                    </View>
                    <View style={[styles.subHeaderCell, { borderRightWidth: 0.6 }]}>
                      <Text style={styles.subHeaderText}>P2</Text>
                    </View>
                    <View style={[styles.subHeaderCell, { borderRightWidth: 0.6 }]}>
                      <Text style={styles.subHeaderText}>P3</Text>
                    </View>
                    <View style={[styles.subHeaderCell, { borderRightWidth: 0.6 }]}>
                      <Text style={styles.subHeaderText}>P4</Text>
                    </View>
                    <View style={[styles.subHeaderCell, { borderRightWidth: 0.6 }]}>
                      <Text style={styles.subHeaderText}>PR</Text>
                    </View>
                    <View style={styles.subHeaderCell}>
                      <Text style={styles.subHeaderText}>REC</Text>
                    </View>
                  </View>
                ))}
                {mostrarPromedioYRanking && (
                  <>
                    <View style={styles.dataCellPromedio}></View>
                    <View style={styles.dataCellMerito}></View>
                  </>
                )}
              </View>

              {/* Filas de estudiantes */}
              {dataConPromedio.map((item, index) => {
                const merito =
                  dataOrdenadaPorMerito.findIndex(
                    d => d.estudiante._id === item.estudiante._id
                  ) + 1;

                return (
                  <View
                    key={item.estudiante._id}
                    style={[
                      styles.dataRow,
                      index % 2 === 0 ? styles.dataRowAlt : styles.dataRowEven,
                    ]}
                    wrap={false}
                  >
                    {mostrarNombres && (
                      <>
                        <View style={styles.dataCellNum}>
                          <Text style={styles.dataText}>{index + 1}</Text>
                        </View>
                        <View style={styles.dataCellName}>
                          <Text style={styles.nameText}>
                            {`${item.estudiante.apellidos}, ${item.estudiante.nombres}`.toUpperCase()}
                          </Text>
                        </View>
                      </>
                    )}
                    {grupoMaterias.map((materia, idx) => {
                      const notaMateria = item.notas.find(
                        n => n.materiaNombre === materia
                      );

                      // Validar que el valor existe y es un número válido antes de parsearlo
                      const p1 = (notaMateria?.bimestre1 !== null && notaMateria?.bimestre1 !== undefined && notaMateria?.bimestre1 > 0)
                        ? parseFloat(notaMateria.bimestre1) : null;
                      const p2 = (notaMateria?.bimestre2 !== null && notaMateria?.bimestre2 !== undefined && notaMateria?.bimestre2 > 0)
                        ? parseFloat(notaMateria.bimestre2) : null;
                      const p3 = (notaMateria?.bimestre3 !== null && notaMateria?.bimestre3 !== undefined && notaMateria?.bimestre3 > 0)
                        ? parseFloat(notaMateria.bimestre3) : null;
                      const p4 = (notaMateria?.bimestre4 !== null && notaMateria?.bimestre4 !== undefined && notaMateria?.bimestre4 > 0)
                        ? parseFloat(notaMateria.bimestre4) : null;
                      const promedio = (notaMateria?.promedio !== null && notaMateria?.promedio !== undefined && notaMateria?.promedio > 0)
                        ? parseFloat(notaMateria.promedio) : null;
                      const recuperacion = (notaMateria?.recuperacion !== null && notaMateria?.recuperacion !== undefined && notaMateria?.recuperacion > 0)
                        ? parseFloat(notaMateria.recuperacion) : null;

                      const p1Color = p1 !== null ? getGradeColor(p1) : '#95a5a6';
                      const p2Color = p2 !== null ? getGradeColor(p2) : '#95a5a6';
                      const p3Color = p3 !== null ? getGradeColor(p3) : '#95a5a6';
                      const p4Color = p4 !== null ? getGradeColor(p4) : '#95a5a6';
                      const promedioColor = promedio !== null ? getGradeColor(promedio) : '#95a5a6';

                      return (
                        <View
                          key={idx}
                          style={{
                            flexDirection: 'row',
                            flex: 1,
                            borderRightWidth: 0.8,
                            borderRightColor: '#dde5f0',
                          }}
                        >
                          <View style={[styles.dataCellNota, { borderRightWidth: 0.6 }]}>
                            <Text style={[styles.notaCell, { color: p1Color }]}>
                              {p1 !== null ? p1.toFixed(1) : '—'}
                            </Text>
                          </View>
                          <View style={[styles.dataCellNota, { borderRightWidth: 0.6 }]}>
                            <Text style={[styles.notaCell, { color: p2Color }]}>
                              {p2 !== null ? p2.toFixed(1) : '—'}
                            </Text>
                          </View>
                          <View style={[styles.dataCellNota, { borderRightWidth: 0.6 }]}>
                            <Text style={[styles.notaCell, { color: p3Color }]}>
                              {p3 !== null ? p3.toFixed(1) : '—'}
                            </Text>
                          </View>
                          <View style={[styles.dataCellNota, { borderRightWidth: 0.6 }]}>
                            <Text style={[styles.notaCell, { color: p4Color }]}>
                              {p4 !== null ? p4.toFixed(1) : '—'}
                            </Text>
                          </View>
                          <View style={[styles.dataCellNota, { borderRightWidth: 0.6, backgroundColor: '#fffbf0' }]}>
                            <Text style={[styles.dataTextBold, { color: promedioColor }]}>
                              {promedio !== null ? promedio.toFixed(2) : '—'}
                            </Text>
                          </View>
                          <View style={[styles.dataCellNota, { backgroundColor: '#f0f8ff' }]}>
                            <Text style={styles.dataTextBold}>
                              {recuperacion !== null ? recuperacion.toFixed(1) : '—'}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                    {mostrarPromedioYRanking && (
                      <>
                        <View style={styles.dataCellPromedio}>
                          <Text style={[styles.dataTextBold, {
                            color: item.promedioGeneral > 0 ? getGradeColor(item.promedioGeneral) : '#0f3460'
                          }]}>
                            {item.promedioGeneral > 0
                              ? item.promedioGeneral.toFixed(2)
                              : '—'}
                          </Text>
                        </View>
                        <View style={styles.dataCellMerito}>
                          <Text style={styles.meritoText}>{merito}</Text>
                        </View>
                      </>
                    )}
                  </View>
                );
              })}
            </View>

            {/* Escala de valoración - Solo en la última página */}
            {esUltimaPagina && (
              <View style={styles.scaleContainer}>
                <Text style={styles.scaleTitle}>ESCALA DE VALORACIÓN</Text>
                <View style={styles.scaleRow}>
                  <View style={[styles.scaleItem, { backgroundColor: '#E74C3C' }]}>
                    <Text style={styles.scaleItemText}>BAJO</Text>
                    <Text style={styles.scaleRange}>1.0 - 2.9</Text>
                  </View>
                  <View style={[styles.scaleItem, { backgroundColor: '#F39C12' }]}>
                    <Text style={styles.scaleItemText}>BÁSICO</Text>
                    <Text style={styles.scaleRange}>3.0 - 3.9</Text>
                  </View>
                  <View style={[styles.scaleItem, { backgroundColor: '#2ECC71' }]}>
                    <Text style={styles.scaleItemText}>ALTO</Text>
                    <Text style={styles.scaleRange}>4.0 - 4.6</Text>
                  </View>
                  <View style={[styles.scaleItem, { backgroundColor: '#27AE60' }]}>
                    <Text style={styles.scaleItemText}>SUPERIOR</Text>
                    <Text style={styles.scaleRange}>4.7 - 5.0</Text>
                  </View>
                </View>
              </View>
            )}

            {/* Pie de página */}
            <View style={styles.footerContainer}>
              <View
                style={{ flexDirection: 'row', justifyContent: 'space-between' }}
              >
                <View>
                  <Text style={styles.footerText}>
                    Promedio del Grado:{' '}
                    <Text style={styles.footerValue}>{promedioDelGrado}</Text>
                  </Text>
                  <Text style={styles.footerText}>
                    Total Evaluados:{' '}
                    <Text style={styles.footerValue}>{dataConPromedio.length}</Text>
                  </Text>
                </View>
                <View>
                  <Text style={styles.footerText}>
                    Fecha:{' '}
                    <Text style={styles.footerValue}>
                      {new Date().toLocaleDateString('es-ES')}
                    </Text>
                  </Text>
                </View>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

const ConsolidadoGeneralReporteButton = ({ datos, dato_grado, dato_periodo }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const dispatch = useDispatch();
  const { sedeSeleccionada } = useSelector(state => state.auth);
  const { configuracion } = useSelector(state => state.configuraciones);

  useEffect(() => { dispatch(getAllConfiguraciones()) }, [dispatch]);

  const generateAndDownloadPdf = async () => {
    if (!datos || datos.length === 0) {
      CustomToast({
        title: 'Error',
        message: 'No hay datos para generar el consolidado',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }

    setIsGenerating(true);

    try {
      const getData = await dispatch(
        obtenerConsolidadoGeneralPorGrado(datos)
      ).unwrap();

      if (!getData || !getData.length) {
        throw new Error(
          'No se encontraron datos de calificaciones para este grupo'
        );
      }

      const pdfDoc = (
        <ConsolidadoGeneralReporte
          data={getData}
          grado={dato_grado?.nombre || '—'}
          sede={sedeSeleccionada?.nombre || '—'}
          tutor={dato_grado?.docente_titular?.nombre || '—'}
          anio={dato_periodo?.year || '—'}
          configuracion={configuracion}
        />
      );

      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      const fileName = `consolidado_general_${dato_grado?._id}_${new Date().toISOString().slice(0, 10)}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      CustomToast({
        title: 'Éxito',
        message: 'Consolidado de notas generado correctamente',
        type: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      CustomToast({
        title: 'Error',
        message: error.message || 'Error al generar el consolidado de notas',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip label="Descargar consolidado de calificaciones" placement="auto">
      <Button
        colorScheme="primary"
        size="md"
        mt={6}
        leftIcon={<FiFileText />}
        variant="solid"
        aria-label="Generar consolidado PDF"
        isLoading={isGenerating}
        onClick={generateAndDownloadPdf}
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      >
        Generar Consolidado General
      </Button>
    </Tooltip>
  );
};

export default ConsolidadoGeneralReporteButton;
